import { CalendarDays, Sparkles } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { PAGE_SEO } from "../lib/seo";
import { SPECIALS } from "../lib/specials";

const DailySpecials = () => {
  usePageMeta(PAGE_SEO.dailySpecials);
  const today = new Date().getDay(); // 0=Sun ... 6=Sat
  const todaySpecial = SPECIALS.find((s) => s.day === today);

  return (
    <main data-testid="page-daily-specials" className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
      {/* HERO */}
      <section className="text-center fade-up mb-12">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#ff1a8c]/60 text-[0.7rem] uppercase tracking-[0.28em] text-[#ff1a8c] mb-5">
          <CalendarDays className="w-3.5 h-3.5" />
          Daily Specials
        </span>
        <h1 className="font-script text-6xl sm:text-7xl lg:text-[7rem] neon-gradient-text leading-[0.95]">
          A treat for<br />every day.
        </h1>
        <p className="text-gray-300 mt-6 max-w-xl mx-auto leading-relaxed">
          New deal, new flavour, new reason to come in. Check what&apos;s on today.
        </p>
      </section>

      {/* TODAY'S HIGHLIGHT */}
      {todaySpecial && (
        <section
          data-testid="todays-special"
          className="mb-14 rounded-3xl border-2 border-[#ff1a8c] bg-gradient-to-br from-[#ff1a8c]/15 via-transparent to-[#00b4ff]/10 p-8 sm:p-10 shadow-[0_0_50px_rgba(255,26,140,0.25)] fade-up"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
            <div>
              <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#00b4ff] font-bold">
                <Sparkles className="w-3.5 h-3.5" /> On today
              </span>
              <p className="font-script text-5xl sm:text-6xl text-white mt-1 leading-none">
                {todaySpecial.label}
              </p>
            </div>
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#ff1a8c] border border-[#ff1a8c]/60 rounded-full px-3 py-1 self-start sm:self-auto">
              Live now
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">
            {todaySpecial.title}
          </h2>
          <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
            {todaySpecial.blurb}
          </p>
        </section>
      )}

      {/* FULL WEEK GRID */}
      <section className="mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-[#00b4ff] mb-6">The full week</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="specials-grid">
          {SPECIALS.map((s) => {
            const isToday = s.day === today;
            const isEveryday = s.day === null;
            return (
              <article
                key={s.key}
                data-testid={`special-card-${s.key}`}
                className={`rounded-2xl p-7 border transition-all duration-300 ${
                  isToday
                    ? "border-[#ff1a8c]/60 bg-[#ff1a8c]/10 shadow-[0_0_28px_rgba(255,26,140,0.2)]"
                    : isEveryday
                    ? "border-[#00b4ff]/40 bg-[#00b4ff]/5 hover:border-[#00b4ff]/70"
                    : "border-white/10 bg-black/30 hover:border-white/30"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span
                    className={`text-[10px] uppercase tracking-[0.28em] font-bold ${
                      isEveryday ? "text-[#00b4ff]" : "text-[#ff1a8c]"
                    }`}
                  >
                    {s.label}
                  </span>
                  {isToday && (
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#ff1a8c] border border-[#ff1a8c]/60 rounded-full px-2.5 py-0.5">
                      Today
                    </span>
                  )}
                  {isEveryday && (
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#00b4ff] border border-[#00b4ff]/60 rounded-full px-2.5 py-0.5">
                      All week
                    </span>
                  )}
                </div>
                <h3 className="font-semibold text-white text-lg mb-2">{s.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{s.blurb}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="text-center mt-14 fade-up">
        <p className="text-sm text-gray-400 mb-3 uppercase tracking-[0.22em]">
          Closed Mondays · open the rest of the week
        </p>
        <a href="/menus" data-testid="daily-specials-menu-cta" className="neon-btn inline-flex">
          See the full menu
        </a>
      </section>
    </main>
  );
};

export default DailySpecials;
